import {
  Component,
  ElementRef,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  Renderer2,
  ViewChild,
  Type,
  ComponentFactoryResolver,
  Input,
  AfterViewChecked,
} from '@angular/core';
import { ElFocusTrap, ElFocusTrapFactoryService } from '../cdk/a11y/focus-trap';
import { ElComponentPortal, ElComponentType, ElTemplatePortal } from '../cdk/overlay/mapping';
import { ElOverlayContainerComponent } from '../cdk/overlay/overlay-container';
import { EL_WINDOW_CONTENT, ElWindowConfig, ElWindowState, EL_WINDOW_CONTEXT } from './window.options';
import { ElWindowRef } from './window-ref';

@Component({
  selector: 'el-window',
  template: `
    <el-card>
      <el-card-header>
        <div cdkFocusInitial class="title" tabindex="-1">{{ config.title }}</div>

        <div class="buttons">
          <button elButton ghost (click)="minimize()">
            <el-icon icon="minus-outline" pack="endlessjs-essentials"></el-icon>
          </button>
          <button elButton ghost *ngIf="isFullScreen" (click)="maximize()">
            <el-icon icon="collapse-outline" pack="endlessjs-essentials"></el-icon>
          </button>
          <button elButton ghost *ngIf="minimized || maximized" (click)="maximizeOrFullScreen()">
            <el-icon icon="expand-outline" pack="endlessjs-essentials"></el-icon>
          </button>
          <button elButton ghost (click)="close()">
            <el-icon icon="close-outline" pack="endlessjs-essentials"></el-icon>
          </button>
        </div>
      </el-card-header>
      <el-card-body *ngIf="maximized || isFullScreen">
        <el-overlay-container></el-overlay-container>
      </el-card-body>
    </el-card>
  `,
  styleUrls: ['./window.component.css'],
})
export class ElWindowComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() cfr: ComponentFactoryResolver;

  @HostBinding('class.full-screen')
  get isFullScreen() {
    return this.windowRef.state === ElWindowState.FULL_SCREEN;
  }

  @HostBinding('class.maximized')
  get maximized() {
    return this.windowRef.state === ElWindowState.MAXIMIZED;
  }

  @HostBinding('class.minimized')
  get minimized() {
    return this.windowRef.state === ElWindowState.MINIMIZED;
  }

  @ViewChild(ElOverlayContainerComponent, { static: false }) overlayContainer: ElOverlayContainerComponent;

  protected focusTrap: ElFocusTrap;

  constructor(
    @Inject(EL_WINDOW_CONTENT) public content: TemplateRef<any> | ElComponentType,
    @Inject(EL_WINDOW_CONTEXT) public context: Object,
    public windowRef: ElWindowRef,
    public config: ElWindowConfig,
    protected focusTrapFactory: ElFocusTrapFactoryService,
    protected elementRef: ElementRef,
    protected renderer: Renderer2,
  ) {}

  ngOnInit() {
    this.focusTrap = this.focusTrapFactory.create(this.elementRef.nativeElement);
    this.focusTrap.blurPreviouslyFocusedElement();
    this.focusTrap.focusInitialElement();

    if (this.config.windowClass) {
      this.renderer.addClass(this.elementRef.nativeElement, this.config.windowClass);
    }
  }

  ngAfterViewChecked() {
    if (!this.overlayContainer || this.overlayContainer.isAttached) {
      return;
    }

    if (this.content instanceof TemplateRef) {
      this.attachTemplate();
    } else {
      this.attachComponent();
    }
  }

  ngOnDestroy() {
    if (this.focusTrap) {
      this.focusTrap.restoreFocus();
    }

    this.close();
  }

  minimize() {
    if (this.windowRef.state === ElWindowState.MINIMIZED) {
      this.windowRef.toPreviousState();
    } else {
      this.windowRef.minimize();
    }
  }

  maximize() {
    this.windowRef.maximize();
  }

  fullScreen() {
    this.windowRef.fullScreen();
  }

  maximizeOrFullScreen() {
    if (this.windowRef.state === ElWindowState.MINIMIZED) {
      this.maximize();
    } else {
      this.fullScreen();
    }
  }

  close() {
    this.windowRef.close();
  }

  protected attachTemplate() {
    this.overlayContainer
      .attachTemplatePortal(new ElTemplatePortal(this.content as TemplateRef<any>, null, this.context));
  }

  protected attachComponent() {
    const portal = new ElComponentPortal(this.content as Type<any>, null, null, this.cfr);
    const ref = this.overlayContainer.attachComponentPortal(portal, this.context);
    ref.changeDetectorRef.detectChanges();
  }
}
